import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useEntityLinkProps } from '../components/links/useEntityLinkProps';
import {
  Card,
  CardHeader,
  Title1,
  Title3,
  Text,
  Badge,
  Button,
  Checkbox,
  Textarea,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Input,
  tokens,
  Divider,
  Tab,
  TabList,
  Link,
  Tooltip,
  Dropdown,
  Option,
  Toast,
  ToastTitle,
  Toaster,
  useToastController,
  useId,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { LoadFailed } from '../components/common/LoadFailed';
import {
  ArrowLeftRegular,
  CheckmarkCircleRegular,
  PlayRegular,
  DocumentRegular,
  AttachRegular,
  MailRegular,
  OpenRegular,
  DeleteRegular,
  AddRegular,
  SaveRegular,
  DocumentPdfRegular,
  EditRegular,
  DismissCircleRegular,
  MoreHorizontalRegular,
  PersonRegular,
  TranslateRegular,
  FlagRegular,
  FlagOffRegular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Radio, RadioGroup } from '@fluentui/react-components';
import {
  useClientReview,
  useMonthReadiness,
  useMarkReviewOpened,
  useUpdateReview,
  useUpdateChecklist,
  useStartReview,
  useCompleteReview,
  useSendReviewEmail,
  usePreviewReviewEmail,
  useUploadDocument,
  useDeleteDocument,
  useReassignReviewer,
} from '../hooks/useReviews';
import { useReviewerEligibleStaff } from '../hooks/useSystemUsers';
import { useCreateActionItem, useActionItems, useCompleteActionItem, useSetReviewMeetingFlag } from '../hooks/useMeetings';
import { useMsal } from '@azure/msal-react';
import DOMPurify from 'dompurify';
import { formatCalendarDate, formatPeriodRange } from '../utils/calendarDate';
import i18n, { useTranslation } from '../i18n';
import { formatInstant } from '../i18n/dates';
import {
  CancelReviewDialog,
  CancellationNote,
  CoverageBanner,
  DeleteReviewDialog,
  EditPeriodDialog,
  MergeOffer,
  PeriodHistoryNote,
  ReinstateOffer,
  apiErrorText,
} from '../components/reviews/ReviewCoverageControls';
import type { ChecklistItem, ClientReview, ReviewDocument, EmailPreviewResponse, StatementPeriodRequest } from '../api/reviews';
import { StatementPeriodField } from '../components/reviews/StatementPeriodField';
import { monthsAsWords, runLabel } from '../utils/reviewMonths';
import { defaultActionItemTitle } from '../utils/actionItemFields';
import { sendThenComplete } from '../utils/sendThenComplete';
import {
  choiceKey,
  isUsable,
  toRequest as toStatementRequest,
  yearToDate,
  type StatementChoice,
} from '../utils/statementPeriod';
import { LANGUAGE_OPTIONS } from '../api/reviews';
import type { ActionItemCategory, ActionItemPriority, MeetingActionItem } from '../types';
import { PageSkeleton } from '../components/common/PageSkeleton';
import { ClientAnnouncementBanner } from '../components/common/ClientAnnouncementBanner';
import { stripSignatureHtml } from '../utils/emailSignature';
import ptcLogoUrl from '../assets/ptc-logo.png';
import { MultiFileUpload } from '../components/upload/MultiFileUpload';
import { MonthStrip } from '../components/reviews/MonthStrip';
import { NextUpBanner } from '../components/reviews/NextUp';
import { SendToClientAction } from '../components/reviews/SendToClientAction';
import { sendToClientGate } from '../components/reviews/sendGate';
import { REVIEW_DOCUMENT_RULES, REVIEW_DOCUMENT_TYPES, documentTypeLabel, REVIEW_UPLOAD_CONCURRENCY } from '../components/upload/reviewDocumentUpload';
import { CalendarDateInput } from '../components/dates/CalendarDateInput';

/**
 * Renders the email-preview body for in-app display (SUP-SIG-02). The backend's preview
 * response carries the real branded-signature HTML verbatim — including the `cid:ptclogo`
 * reference that only resolves inside an email client once Graph attaches the inline logo on
 * send (see GraphService.BuildInlineLogoIfReferenced). Rendered as plain text, that HTML would
 * show up as literal `<p>`/`<img>` tag soup instead of the signature; rendered as HTML with the
 * `cid:` reference left alone, the browser has nothing to resolve it against and shows a broken
 * image icon. So for PREVIEW DISPLAY ONLY we substitute a bundled, hosted copy of the same PTC
 * logo image; the value actually sent by SendReviewEmail is untouched and still carries
 * `cid:ptclogo` (GraphService still attaches the inline logo for the real send).
 */
function buildPreviewBodyHtml(body: string): string {
  const withMarkup = body
    .replace(/\n/g, '<br/>')
    .replace(/cid:ptclogo/g, ptcLogoUrl);
  // Defense-in-depth: sanitize before this HTML is bound via dangerouslySetInnerHTML, stripping any
  // active content (scripts, event-handler attributes, javascript: URLs). The backend now
  // HTML-escapes the client name too (EmailFunctions), so this is a second layer — not the only
  // one. DOMPurify's defaults preserve the branded signature markup (p/table/tr/td/span/img/br and
  // sanitized inline styles) and the hosted logo URL.
  return DOMPurify.sanitize(withMarkup);
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge appearance="filled" color="success" size="large">{i18n.t('reviewStatus.completed')}</Badge>;
    case 'in-progress':
      return <Badge appearance="filled" color="brand" size="large">{i18n.t('reviewStatus.inProgress')}</Badge>;
    case 'ready':
      return <Badge appearance="tint" color="warning" size="large">{i18n.t('reviewStatus.ready')}</Badge>;
    // Created and visible, but the period's lead-in has not arrived — not yet actionable.
    case 'scheduled':
      return <Badge appearance="outline" color="informative" size="large">{i18n.t('reviewStatus.scheduled')}</Badge>;
    case 'waiting-on-bookkeeping': // DP-READY-3: period ended, bookkeeping still open for one of its months
      return <Badge appearance="outline" color="warning" size="large">{i18n.t('reviewStatus.waitingOnBookkeeping')}</Badge>;
    case 'pending': // legacy response with no derived status
      return <Badge appearance="tint" color="warning" size="large">{i18n.t('reviewStatus.pending')}</Badge>;
    default:
      return <Badge appearance="outline" size="large">{status}</Badge>;
  }
}

/**
 * Who created this review record. Absent on every pre-2026-07-25 document — those were deliberately
 * NOT backfilled, so "—" here means genuinely unknown rather than nobody.
 */
function formatReviewAuthor(review: Pick<ClientReview, 'createdBy' | 'createdBySource'>): string {
  if (review.createdBySource === 'autogen') return i18n.t('reviewDetail.author.autogen');
  return review.createdBy?.trim() || i18n.t('reviewDetail.author.unknown');
}

interface ChecklistSectionProps {
  items: ChecklistItem[];
  onToggle: (itemId: string, isChecked: boolean) => void;
  disabled?: boolean;
}

function ChecklistSection({ items, onToggle, disabled }: ChecklistSectionProps) {
  const categories = [...new Set(items.map(i => i.category))];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {categories.map(category => (
        <div key={category}>
          <Text weight="semibold" style={{ textTransform: 'capitalize', marginBottom: '8px', display: 'block' }}>
            {category.replace('-', ' ')}
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {items
              .filter(i => i.category === category)
              .sort((a, b) => a.order - b.order)
              .map(item => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: item.isChecked ? tokens.colorNeutralBackground3 : tokens.colorNeutralBackground2,
                    borderRadius: '4px',
                  }}
                >
                  <Checkbox
                    checked={item.isChecked}
                    onChange={(_, data) => onToggle(item.id, data.checked as boolean)}
                    disabled={disabled}
                  />
                  <div style={{ flex: 1 }}>
                    <Text style={{ textDecoration: item.isChecked ? 'line-through' : 'none' }}>
                      {item.title}
                    </Text>
                    {item.description && (
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                        {item.description}
                      </Text>
                    )}
                  </div>
                  {item.checkedAt && (
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      {formatInstant(item.checkedAt, 'dayTime')}
                    </Text>
                  )}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface FileUploadSectionProps {
  title: string;
  documents: ReviewDocument[];
  /** Sends one file; throws on failure so the file's own row can say why (upload primitive U5). */
  onUpload: (file: File, docType: string) => Promise<unknown>;
  onDelete: (docId: string) => void;
  disabled?: boolean;
}

// Financial documents only — the "AI Review" surface (tab + ai-review document
// type) was cut in 2026-07 (feature-flagged off, never used). Stored ai-review
// documents keep deserializing server-side; only the upload/view UI is gone.
function FileUploadSection({ title, documents, onUpload, onDelete, disabled }: FileUploadSectionProps) {
  const { t } = useTranslation();
  return (
    <Card style={{ padding: '16px' }}>
      <CardHeader
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DocumentPdfRegular />
            <Text weight="semibold">{title}</Text>
          </div>
        }
      />
      {/* The fleet upload primitive (docs/upload-primitive.md): drop or choose several files, each
          typed from its name, checked against the server's own limits, and reported on its own row. */}
      {!disabled && (
        <div style={{ marginTop: '12px' }}>
          <MultiFileUpload
            types={REVIEW_DOCUMENT_TYPES}
            rules={REVIEW_DOCUMENT_RULES}
            existingNames={documents.map((d) => d.fileName)}
            upload={onUpload}
            describeError={apiErrorText}
            // One at a time: concurrent uploads race on the review document (see the constant).
            concurrency={REVIEW_UPLOAD_CONCURRENCY}
          />
        </div>
      )}
      <div style={{ marginTop: '12px' }}>
        {documents.length === 0 ? (
          <Text style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>
            {t('reviewDetail.noDocuments')}
          </Text>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {documents.map(doc => (
              <div
                key={doc.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: tokens.colorNeutralBackground2,
                  borderRadius: '4px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DocumentRegular />
                  <div>
                    <Text>{doc.fileName}</Text>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                      {documentTypeLabel(doc.documentType)} - {formatInstant(doc.uploadedAt, 'dayYear')}
                    </Text>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <Button
                    as="a"
                    appearance="subtle"
                    size="small"
                    icon={<OpenRegular />}
                    href={doc.blobUrl}
                    target="_blank"
                    rel="noopener"
                    aria-label={t('reviewDetail.documents.open', { name: doc.fileName })}
                  />
                  {!disabled && (
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DeleteRegular />}
                      onClick={() => onDelete(doc.id)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

interface SendEmailDialogProps {
  open: boolean;
  onClose: () => void;
  reviewId: string;
  clientId: string;
  clientName: string;
  /** The review's period end: the statements default to year-to-date of that year (spec §10.13.4). */
  reviewPeriodEnd: string;
  // Must reject on failure (F3): the dialog awaits the send and only clears the draft +
  // closes once the promise resolves — a rejection keeps the dialog open with the draft
  // intact and surfaces the error inline.
  onSend: (data: SendEmailData) => Promise<void>;
  /**
   * Present when the review can be completed from here (it is in progress): the dialog then offers "also mark this
   * review complete" (FB-10, Christian 2026-10-29), checked when the checklist is complete and unchecked otherwise.
   * Sending never completes a review silently; the completion is the ordinary one, with the ordinary guard.
   */
  completion?: { checklistDone: number; checklistTotal: number };
}

export type SendEmailData = { recipientEmail: string; subject: string; body: string; targetLanguage?: string; alsoComplete: boolean }
  & StatementPeriodRequest;

interface EmailTemplateOption {
  id: string;
  templateKey: string;
  name: string;
  description?: string;
}

// Exported for tests (SendEmailDialog.test.tsx) — rendered only by ReviewDetailPage.
export function SendEmailDialog({ open, onClose, reviewId, clientName, reviewPeriodEnd, onSend, completion }: SendEmailDialogProps) {
  const { t } = useTranslation();
  // The reviewer's own choice, once made; until then the default follows the checklist.
  const [alsoCompleteChoice, setAlsoCompleteChoice] = useState<boolean | null>(null);
  const checklistComplete = !!completion && completion.checklistTotal > 0 && completion.checklistDone === completion.checklistTotal;
  const alsoComplete = !!completion && (alsoCompleteChoice ?? checklistComplete);
  const [step, setStep] = useState<'input' | 'preview'>('input');
  // The statement period (spec §10.13.4): YTD unless explicitly custom. `draftFor` is the choice the current
  // draft was written for; while the reviewer has not edited the text, the draft follows the choice.
  const [statement, setStatement] = useState<StatementChoice>(() => yearToDate(reviewPeriodEnd));
  const [draftFor, setDraftFor] = useState<StatementChoice>(() => yearToDate(reviewPeriodEnd));
  const draftEditedRef = useRef(false);
  const statementUsable = isUsable(statement);
  const draftIsStale = statementUsable && choiceKey(statement) !== choiceKey(draftFor);
  useEffect(() => {
    if (draftIsStale && !draftEditedRef.current) setDraftFor(statement);
  }, [draftIsStale, statement]);
  const [recipientEmail, setRecipientEmail] = useState('');
  // The subject is the server's, in the CLIENT's language, until the reviewer edits it; then it is theirs.
  const [subject, setSubject] = useState('');
  const subjectEditedRef = useRef(false);
  const [emailBody, setEmailBody] = useState('');
  const [preview, setPreview] = useState<EmailPreviewResponse | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [templates, setTemplates] = useState<EmailTemplateOption[]>([]);
  // The template list failed to load (P6, 2026-10-30): said under the dropdown, not only in the console.
  const [templatesFailed, setTemplatesFailed] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('financial-statements');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // The language the CLIENT is written to (fleet ruling 2026-10-27): seeded from the client's CRM record as the
  // server reads it live (never from the review document, whose copy went stale), one of en/es/pt. When the CRM
  // records none, nothing is preselected and Preview/Send wait for a choice: an unknown language is never English
  // by default. A choice applies to THIS email only; the client's language changes in the CRM, nowhere else.
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  // undefined = not yet known; null = the CRM records no (en/es/pt) language for this client.
  const [clientLanguage, setClientLanguage] = useState<string | null | undefined>(undefined);
  // Tracks whether the supervisor explicitly picked a language in THIS dialog session — once
  // they have, the preview-resolved reseed (F11) must not override their choice.
  const userPickedLanguageRef = useRef(false);

  // There is no staff-language lookup here. The editor body is the clean, UNTRANSLATED draft and is
  // translated to `selectedLanguage` (the client's) at send; a "viewing in <staff language>" badge,
  // read from the staff record, claimed otherwise and was removed (ruling 2026-10-21: nothing is
  // read from a staff record).

  const previewEmailMutation = usePreviewReviewEmail();

  // Fetch available templates when dialog opens
  useEffect(() => {
    if (open) {
      setTemplatesFailed(false);
      // Fetch templates list
      fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/email-templates?project=ptc-supervision`)
        .then(res => {
          return res.json();
        })
        .then(result => {
          if (result.success && result.data) {
            const templateList = result.data.map((t: any) => ({
              id: t.id,
              templateKey: t.templateKey,
              name: t.name,
              description: t.description,
            }));
            setTemplates(templateList);
            setTemplatesFailed(false);

            // Auto-select financial-statements if available
            const financialTemplate = templateList.find((t: EmailTemplateOption) => t.templateKey === 'financial-statements');
            if (financialTemplate) {
              setSelectedTemplateKey(financialTemplate.templateKey);
            }
          }
        })
        .catch(err => {
          console.error('[SendEmailDialog] Failed to fetch templates:', err);
          setTemplatesFailed(true);
        });
    }
  }, [open]);

  // Fetch template content and client email when template is selected
  useEffect(() => {
    if (open && selectedTemplateKey) {
      setIsLoadingTemplate(true);

      // No subject is sent: the server composes the client-facing subject, in the CLIENT's path (English), with
      // the statement period in it. The staff UI language never rewrites it.
      previewEmailMutation.mutateAsync({
        id: reviewId,
        request: { ...toStatementRequest(draftFor) },
      })
        .then((result: any) => {
          // Handle both camelCase and PascalCase from API.
          // The editor draft must be the CLEAN, unsigned, untranslated body (SUP-SIG-03):
          // prefer originalBody (the backend's clean draft field) and defensively strip any
          // signature markup an older backend may still send in body — a signed draft would
          // gain another signature on every subsequent Preview.
          const draftBody =
            result.originalBody || result.OriginalBody || result.body || result.Body;
          const clientEmail = result.clientEmail || result.ClientEmail;
          const emailSubject = result.subject || result.Subject;

          // Auto-populate recipient email if available
          if (clientEmail) {
            setRecipientEmail(clientEmail);
          }
          // Set the subject from API response
          if (emailSubject) {
            setSubject(emailSubject);
          }
          // Set the email body from template
          if (draftBody) {
            setEmailBody(stripSignatureHtml(draftBody));
          }
          draftEditedRef.current = false;
          // Seed the picker from the client's CRM language as the SERVER read it live (F11). A server older than
          // the 2026-10-27 ruling sends no `clientLanguage`; then its resolved language stands in. An explicit pick
          // by the reviewer still wins.
          const hasClientLanguage = 'clientLanguage' in result || 'ClientLanguage' in result;
          const recorded: string | null = hasClientLanguage
            ? (result.clientLanguage ?? result.ClientLanguage ?? null)
            : (result.targetLanguage || result.TargetLanguage || null);
          setClientLanguage(recorded);
          if (!userPickedLanguageRef.current) {
            setSelectedLanguage(recorded && LANGUAGE_OPTIONS.some(l => l.code === recorded) ? recorded : '');
          }
          // Store the preview for metadata
          setPreview(result);
        })
        .catch((error) => {
          console.error('[SendEmailDialog] Failed to fetch email template:', error);
          setPreviewError(t('sendEmail.templateLoadFailed', { message: error.message }));
        })
        .finally(() => {
          setIsLoadingTemplate(false);
        });
    }
    // previewEmailMutation.mutateAsync is a stable react-query reference; the
    // template fetch should re-run only when the dialog/template params change,
    // not on every render (which depending on the whole mutation object causes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedTemplateKey, reviewId, clientName, choiceKey(draftFor)]);

  const handleTemplateChange = (templateKey: string) => {
    setSelectedTemplateKey(templateKey);
    setEmailBody(''); // Clear to trigger reload
  };

  const handleLanguageChange = (languageCode: string) => {
    userPickedLanguageRef.current = true;
    setSelectedLanguage(languageCode);
  };

  const handleGetPreview = async () => {
    if (!recipientEmail || !emailBody || !selectedLanguage) return;

    setIsLoadingPreview(true);
    setPreviewError(null);

    try {
      // Use the custom body for translation if needed, pass selected language
      const result: any = await previewEmailMutation.mutateAsync({
        id: reviewId,
        // No subject unless the reviewer edited it: the server writes it in the chosen language.
        request: {
          subject: subjectEditedRef.current ? subject : undefined,
          body: emailBody,
          targetLanguage: selectedLanguage,
          ...toStatementRequest(statement),
        },
      });
      if (!subjectEditedRef.current && (result.subject || result.Subject)) setSubject(result.subject || result.Subject);
      setPreview(result);
      // Deliberately do NOT write the previewed body back into the editor draft (SUP-SIG-03):
      // the preview body carries the translation + branded signature the backend composed, and
      // storing it would make the next Preview append ANOTHER signature and re-translate
      // already-translated text. The draft stays clean; Send submits the clean draft plus the
      // selected target language, and the backend composes the same translated+signed message
      // the preview showed.
      setStep('preview');
    } catch (error) {
      setPreviewError(error instanceof Error ? error.message : t('sendEmail.previewFailed'));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    if (!recipientEmail || !emailBody || !selectedLanguage || isSending) return;
    setIsSending(true);
    setSendError(null);
    try {
      // AWAIT the send (F3): since PR #27 a translation failure is a designed 502 at
      // send-time, so the draft must only be cleared once the send actually succeeded.
      await onSend({
        recipientEmail,
        subject,
        body: emailBody,
        targetLanguage: selectedLanguage,
        alsoComplete,
        ...toStatementRequest(statement),
      });
      handleClose();
    } catch (error) {
      // Keep the dialog open with the draft intact and surface the error inline — the
      // supervisor can fix the issue (or just retry) without retyping anything.
      setSendError(error instanceof Error ? error.message : t('sendEmail.sendFailed'));
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setStep('input');
    setRecipientEmail('');
    setSubject('');
    subjectEditedRef.current = false;
    setClientLanguage(undefined);
    setSelectedLanguage('');
    setEmailBody('');
    setPreview(null);
    setPreviewError(null);
    setSendError(null);
    setSelectedTemplateKey('financial-statements');
    userPickedLanguageRef.current = false;
    setStatement(yearToDate(reviewPeriodEnd));
    setDraftFor(yearToDate(reviewPeriodEnd));
    draftEditedRef.current = false;
    setAlsoCompleteChoice(null);
    onClose();
  };

  const handleBack = () => {
    setStep('input');
    setPreview(null);
    setSendError(null);
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface style={{ maxWidth: '700px' }}>
        <DialogBody>
          {step === 'input' ? (
            <>
              <DialogTitle>{t('sendEmail.title')}</DialogTitle>
              <DialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Field label={t('sendEmail.template')}>
                    <Dropdown
                      value={templates.find(t => t.templateKey === selectedTemplateKey)?.name || t('sendEmail.selectTemplate')}
                      selectedOptions={[selectedTemplateKey]}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) {
                          handleTemplateChange(data.optionValue);
                        }
                      }}
                      disabled={isLoadingTemplate || templates.length === 0}
                    >
                      {templates.map(template => (
                        <Option key={template.templateKey} value={template.templateKey} text={template.name}>
                          <div>
                            <Text weight="semibold">{template.name}</Text>
                            {template.description && (
                              <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                                {template.description}
                              </Text>
                            )}
                          </div>
                        </Option>
                      ))}
                    </Dropdown>
                    {templatesFailed && (
                      <Text role="alert" size={200} style={{ display: 'block', marginTop: '4px', color: tokens.colorPaletteRedForeground1 }}>
                        {t('sendEmail.templatesNotLoaded')}
                      </Text>
                    )}
                  </Field>

                  <Field
                    label={t('sendEmail.recipient')}
                    required
                    hint={isLoadingTemplate ? t('common.loading') : undefined}
                  >
                    <Input
                      type="email"
                      value={recipientEmail}
                      onChange={(_, data) => setRecipientEmail(data.value)}
                      placeholder={isLoadingTemplate ? t('common.loading') : t('sendEmail.recipientPlaceholder')}
                      disabled={isLoadingTemplate}
                    />
                  </Field>
                  <StatementPeriodField
                    reviewPeriodEnd={reviewPeriodEnd}
                    value={statement}
                    onChange={setStatement}
                    disabled={isLoadingTemplate}
                  />
                  {draftIsStale && (
                    <MessageBar intent="warning" data-testid="statement-draft-stale">
                      <MessageBarBody>
                        {t('sendEmail.statement.draftStale')}{' '}
                        <Button
                          size="small"
                          appearance="secondary"
                          onClick={() => {
                            draftEditedRef.current = false;
                            setDraftFor(statement);
                          }}
                        >
                          {t('sendEmail.statement.updateDraft')}
                        </Button>
                      </MessageBarBody>
                    </MessageBar>
                  )}
                  <Field label={t('sendEmail.subject')}>
                    <Input
                      value={subject}
                      onChange={(_, data) => {
                        draftEditedRef.current = true;
                        subjectEditedRef.current = true;
                        setSubject(data.value);
                      }}
                      disabled={isLoadingTemplate}
                    />
                  </Field>
                  <Field
                    label={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('sendEmail.body')}</span>
                      </div>
                    }
                    hint={isLoadingTemplate ? t('sendEmail.loadingTemplate') : t('sendEmail.editHint')}
                  >
                    <Textarea
                      value={emailBody}
                      onChange={(_, data) => {
                        draftEditedRef.current = true;
                        setEmailBody(data.value);
                      }}
                      placeholder={isLoadingTemplate ? t('sendEmail.loadingTemplate') : t('sendEmail.bodyPlaceholder')}
                      disabled={isLoadingTemplate}
                      resize="vertical"
                      style={{ minHeight: '200px' }}
                    />
                  </Field>
                  {previewError && (
                    <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
                      {previewError}
                    </Text>
                  )}

                  <Field label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <TranslateRegular />
                      <span>{t('sendEmail.translationLanguage')}</span>
                    </div>
                  }>
                    <Dropdown
                      value={LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.name ?? ''}
                      placeholder={t('sendEmail.chooseLanguage')}
                      selectedOptions={selectedLanguage ? [selectedLanguage] : []}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) {
                          handleLanguageChange(data.optionValue);
                        }
                      }}
                      disabled={isLoadingTemplate}
                    >
                      {LANGUAGE_OPTIONS.map(lang => (
                        <Option key={lang.code} value={lang.code}>
                          {lang.name}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                  {clientLanguage === null ? (
                    // Blank in the CRM = unknown (ruling 2026-10-27): say so, and ask, rather than send English.
                    <Text size={200} style={{ color: tokens.colorPaletteYellowForeground1 }}>
                      ⚠ {t('sendEmail.languageUnknown')}
                    </Text>
                  ) : (
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      {t('sendEmail.languageFromCrm')}
                    </Text>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">{t('common.cancel')}</Button>
                </DialogTrigger>
                <Button
                  appearance="primary"
                  onClick={handleGetPreview}
                  disabled={!recipientEmail || !emailBody || !selectedLanguage || isLoadingPreview || isLoadingTemplate}
                >
                  {isLoadingPreview ? t('sendEmail.generating') : t('sendEmail.previewAndTranslate')}
                </Button>
              </DialogActions>
            </>
          ) : (
            <>
              <DialogTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {t('sendEmail.previewTitle')}
                  {((preview as any)?.wasTranslated || (preview as any)?.WasTranslated) && (
                    <Badge appearance="tint" color="brand" size="small">
                      {t('sendEmail.translatedTo', { language: (preview as any)?.targetLanguageName || (preview as any)?.TargetLanguageName })}
                    </Badge>
                  )}
                  {/* Honest indicator (F13g): a non-English target whose body the server could
                      only serve from the untranslated English template must not masquerade as
                      translated. */}
                  {preview && selectedLanguage !== 'en' &&
                    !((preview as any)?.wasTranslated || (preview as any)?.WasTranslated) && (
                    <Badge appearance="tint" color="warning" size="small">
                      {t('sendEmail.notTranslated')}
                    </Badge>
                  )}
                </div>
              </DialogTitle>
              <DialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: tokens.colorNeutralBackground2,
                    borderRadius: '4px'
                  }}>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t('sendEmail.to')}</Text>
                    <Text weight="semibold" style={{ display: 'block' }}>{recipientEmail}</Text>
                  </div>

                  <div style={{
                    padding: '12px',
                    backgroundColor: tokens.colorNeutralBackground2,
                    borderRadius: '4px'
                  }}>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t('sendEmail.subjectLabel')}</Text>
                    <Text weight="semibold" style={{ display: 'block' }}>{(preview as any)?.subject || (preview as any)?.Subject}</Text>
                  </div>

                  <div style={{
                    padding: '12px',
                    backgroundColor: tokens.colorNeutralBackground2,
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginBottom: '8px', display: 'block' }}>
                      {t('sendEmail.message')} {((preview as any)?.wasTranslated || (preview as any)?.WasTranslated) ? `(${(preview as any)?.targetLanguageName || (preview as any)?.TargetLanguageName})` : ''}:
                    </Text>
                    <div
                      style={{ whiteSpace: 'normal' }}
                      dangerouslySetInnerHTML={{
                        __html: buildPreviewBodyHtml((preview as any)?.body || (preview as any)?.Body || ''),
                      }}
                    />
                  </div>

                  {((preview as any)?.wasTranslated || (preview as any)?.WasTranslated) && ((preview as any)?.originalBody || (preview as any)?.OriginalBody) && (
                    <details style={{ marginTop: '8px' }}>
                      <summary style={{ cursor: 'pointer', color: tokens.colorNeutralForeground3 }}>
                        {t('sendEmail.showOriginal')}
                      </summary>
                      <div style={{
                        padding: '12px',
                        backgroundColor: tokens.colorNeutralBackground3,
                        borderRadius: '4px',
                        marginTop: '8px',
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        <Text style={{ whiteSpace: 'pre-wrap', color: tokens.colorNeutralForeground2 }}>
                          {(preview as any)?.originalBody || (preview as any)?.OriginalBody}
                        </Text>
                      </div>
                    </details>
                  )}

                  {preview && ((preview as any).attachmentCount > 0 || (preview as any).AttachmentCount > 0) && (
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: tokens.colorNeutralBackground3,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <AttachRegular />
                      <Text size={200}>
                        {t('sendEmail.attachments', {
                          count: (preview as any).attachmentCount || (preview as any).AttachmentCount,
                          names: ((preview as any).attachmentNames || (preview as any).AttachmentNames || []).join(', '),
                        })}
                      </Text>
                    </div>
                  )}

                  {completion && (
                    <div>
                      <Checkbox
                        checked={alsoComplete}
                        onChange={(_, data) => setAlsoCompleteChoice(!!data.checked)}
                        disabled={isSending}
                        label={t('sendEmail.alsoComplete')}
                      />
                      <Text block size={200} style={{ color: tokens.colorNeutralForeground3, marginLeft: '32px' }}>
                        {checklistComplete
                          ? t('sendEmail.alsoCompleteChecklistDone')
                          : t('sendEmail.alsoCompleteChecklistOpen', { done: completion.checklistDone, total: completion.checklistTotal })}
                      </Text>
                    </div>
                  )}

                  {sendError && (
                    <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
                      {sendError}
                    </Text>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={handleBack} disabled={isSending}>
                  {t('common.back')}
                </Button>
                <Button
                  appearance="primary"
                  icon={<MailRegular />}
                  onClick={handleSend}
                  disabled={isSending}
                >
                  {isSending ? t('sendEmail.sending') : t('sendEmail.send')}
                </Button>
              </DialogActions>
            </>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// Category and priority options for action items
const categoryOptions: { value: ActionItemCategory; labelKey: string }[] = [
  { value: 'reconciliation', labelKey: 'actionItems.category.reconciliation' },
  { value: 'data-entry', labelKey: 'actionItems.category.dataEntry' },
  { value: 'client-communication', labelKey: 'actionItems.category.clientCommunication' },
  { value: 'missing-documents', labelKey: 'actionItems.category.missingDocuments' },
  { value: 'review-finding', labelKey: 'actionItems.category.reviewFinding' },
  { value: 'process', labelKey: 'actionItems.category.process' },
  { value: 'training', labelKey: 'actionItems.category.training' },
  { value: 'other', labelKey: 'actionItems.category.other' },
];

const priorityOptions: { value: ActionItemPriority; labelKey: string; color: string }[] = [
  { value: 'low', labelKey: 'actionItems.priority.low', color: 'success' },
  { value: 'medium', labelKey: 'actionItems.priority.medium', color: 'warning' },
  { value: 'high', labelKey: 'actionItems.priority.high', color: 'severe' },
  { value: 'critical', labelKey: 'actionItems.priority.critical', color: 'danger' },
];

/** A priority the vocabulary knows reads in the UI language; anything else is shown as stored. */
function priorityLabel(p: string): string {
  const opt = priorityOptions.find((o) => o.value === p);
  return opt ? i18n.t(opt.labelKey) : p;
}

interface AddActionItemDialogProps {
  open: boolean;
  onClose: () => void;
  review: {
    id: string;
    clientId: string;
    clientName: string;
    bookkeeperId: string;
    bookkeeperName: string;
    periodStart?: string;
    periodEnd?: string;
  };
  onSubmit: (data: {
    title: string;
    description: string;
    category: ActionItemCategory;
    priority: ActionItemPriority;
    dueDate?: string;
  }) => void;
  isSubmitting: boolean;
}

export function AddActionItemDialog({ open, onClose, review, onSubmit, isSubmitting }: AddActionItemDialogProps) {
  const { t } = useTranslation();
  // The default title is `<client> · <period>` (Christian, 2026-10-28); the reviewer can type over it. Each time the
  // dialog opens it starts from the default again, so a previous item's title never carries into the next one.
  const defaultTitle = defaultActionItemTitle(review.clientName, review.periodStart, review.periodEnd);
  const [title, setTitle] = useState(defaultTitle);
  useEffect(() => {
    if (open) setTitle(defaultTitle);
  }, [open, defaultTitle]);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ActionItemCategory>('review-finding');
  const [priority, setPriority] = useState<ActionItemPriority>('medium');
  const [dueDate, setDueDate] = useState('');

  const handleSubmit = () => {
    onSubmit({
      title,
      description,
      category,
      priority,
      dueDate: dueDate || undefined,
    });
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setCategory('review-finding');
    setPriority('medium');
    setDueDate('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface style={{ maxWidth: '500px' }}>
        <DialogBody>
          <DialogTitle>{t('reviewDetail.addAction.title')}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                {t('reviewDetail.addAction.intro', { bookkeeper: review.bookkeeperName, client: review.clientName })}
              </Text>

              <Field label={t('reviewDetail.addAction.titleLabel')} required>
                <Input
                  value={title}
                  onChange={(_, data) => setTitle(data.value)}
                  placeholder={t('reviewDetail.addAction.titlePlaceholder')}
                />
              </Field>

              <Field label={t('reviewDetail.addAction.description')}>
                <Textarea
                  value={description}
                  onChange={(_, data) => setDescription(data.value)}
                  placeholder={t('reviewDetail.addAction.descriptionPlaceholder')}
                  rows={3}
                />
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Field label={t('reviewDetail.addAction.category')}>
                  <Dropdown
                    value={t(categoryOptions.find(c => c.value === category)?.labelKey ?? 'actionItems.category.other')}
                    onOptionSelect={(_, data) => setCategory(data.optionValue as ActionItemCategory)}
                  >
                    {categoryOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{t(opt.labelKey)}</Option>
                    ))}
                  </Dropdown>
                </Field>

                <Field label={t('reviewDetail.addAction.priority')}>
                  <Dropdown
                    value={priorityLabel(priority)}
                    onOptionSelect={(_, data) => setPriority(data.optionValue as ActionItemPriority)}
                  >
                    {priorityOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{t(opt.labelKey)}</Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>

              <Field label={t('reviewDetail.addAction.dueDate')}>
                <CalendarDateInput
                  value={dueDate}
                  onChange={(value) => setDueDate(value)}
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">{t('common.cancel')}</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={!title.trim() || isSubmitting}
            >
              {isSubmitting ? t('reviewDetail.addAction.creating') : t('reviewDetail.addAction.create')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  // Real links (fleet ruling 2026-10-29): Back is an <a href>, so it opens the list in a new tab on right-click.
  const reviewsLink = useEntityLinkProps('/reviews');
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const currentUser = accounts[0];

  const toasterId = useId('review-detail-toaster');
  const { dispatchToast } = useToastController(toasterId);
  const notifySuccess = (message: string) =>
    dispatchToast(
      <Toast>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
      { intent: 'success' }
    );
  const notifyError = (message: string) =>
    dispatchToast(
      <Toast>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
      { intent: 'error' }
    );

  const [activeTab, setActiveTab] = useState<string>('checklist');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditPeriodDialog, setShowEditPeriodDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelInitialReason, setCancelInitialReason] = useState<string | undefined>(undefined);
  const [showEditTypeDialog, setShowEditTypeDialog] = useState(false);
  const [showSupervisorDialog, setShowSupervisorDialog] = useState(false);
  const [showAddActionItemDialog, setShowAddActionItemDialog] = useState(false);
  const [pendingDeleteDocId, setPendingDeleteDocId] = useState<string | null>(null);
  // The chosen reviewer's systemuserid: a person from the reviewer list, never a typed name.
  const [newSupervisorId, setNewSupervisorId] = useState('');
  const [notes, setNotes] = useState('');
  const [findings, setFindings] = useState('');

  const { data: review, isLoading, error } = useClientReview(id);
  // Its own fail-soft request: the review never waits for it (docs/specs/month-readiness.md).
  const { data: monthReadiness } = useMonthReadiness(id);
  // Genuine first-interaction signal for `startedAt`. Sent from the mounted detail page only — the
  // reviews grid's row-hover prefetch shares the detail *query*, so the stamp must not ride on it.
  useMarkReviewOpened(id);
  const updateReviewMutation = useUpdateReview();
  const reassignReviewer = useReassignReviewer();
  const { data: reviewerOptions, error: reviewerOptionsError } = useReviewerEligibleStaff();
  const updateChecklistMutation = useUpdateChecklist();
  const startReviewMutation = useStartReview();
  const completeReviewMutation = useCompleteReview();
  const meetingFlagMutation = useSetReviewMeetingFlag();
  const sendEmailMutation = useSendReviewEmail();
  const createActionItemMutation = useCreateActionItem();
  const completeActionItemMutation = useCompleteActionItem();
  const uploadDocumentMutation = useUploadDocument();
  const deleteDocumentMutation = useDeleteDocument();

  // Fetch action items for this review. Null until they have loaded: a list that is loading or failed is NOT
  // "no action items", and the send and complete gates must not read it as one (P6, 2026-10-30).
  const {
    data: actionItemsData,
    isLoading: actionItemsLoading,
    error: actionItemsError,
    refetch: refetchActionItems,
  } = useActionItems();
  const reviewActionItems: MeetingActionItem[] | null = actionItemsData
    ? actionItemsData.items.filter((item: MeetingActionItem) => item.sourceReviewId === id)
    : null;

  // Sync local state with review data once it loads (and whenever it changes).
  useEffect(() => {
    if (review) {
      setNotes(review.supervisorNotes || '');
      setFindings(review.findings || '');
    }
  }, [review]);

  const handleToggleChecklistItem = async (itemId: string, isChecked: boolean) => {
    if (!id) return;
    await updateChecklistMutation.mutateAsync({
      id,
      request: { itemId, isChecked },
    });
  };

  const handleStartReview = async () => {
    if (!id) return;
    await startReviewMutation.mutateAsync(id);
  };

  const handleCompleteReview = async () => {
    if (!id) return;
    // Save notes first
    await updateReviewMutation.mutateAsync({
      id,
      data: { supervisorNotes: notes, findings },
    });
    await completeReviewMutation.mutateAsync(id);
  };

  const handleSaveNotes = async () => {
    if (!id) return;
    await updateReviewMutation.mutateAsync({
      id,
      data: { supervisorNotes: notes, findings },
    });
  };

  // Throws on failure: the upload primitive shows the reason on that file's row and offers Retry,
  // which a toast cannot do for one file among several.
  const handleFileUpload = async (file: File, docType: string) => {
    if (!id) throw new Error(t('reviewDetail.notLoaded'));
    return uploadDocumentMutation.mutateAsync({
      reviewId: id,
      file,
      documentType: docType,
      uploadedBy: currentUser?.name || 'Supervisor',
    });
  };

  // Opens the confirm dialog; the actual delete happens in
  // confirmDeleteDocument once the user confirms.
  const handleFileDelete = (docId: string) => {
    setPendingDeleteDocId(docId);
  };

  const confirmDeleteDocument = async () => {
    if (!id || !pendingDeleteDocId) return;
    const docId = pendingDeleteDocId;
    setPendingDeleteDocId(null);
    try {
      await deleteDocumentMutation.mutateAsync({
        reviewId: id,
        documentId: docId,
      });
    } catch (error) {
      notifyError(t('reviewDetail.notify.deleteFileFailed', { message: error instanceof Error ? error.message : t('common.unknownError') }));
    }
  };

  // Rethrows on failure (F3): the dialog awaits this, closes + clears the draft only on
  // success, and keeps the dialog open with the draft intact — showing the error inline —
  // when the send fails (e.g. the designed 502 for a failed translation, PR #27).
  const handleSendEmail = async (data: SendEmailData) => {
    if (!id) throw new Error('Missing review id');
    const send = async () => {
      try {
        await sendEmailMutation.mutateAsync({
          id,
          request: {
            recipientEmail: data.recipientEmail,
            subject: data.subject,
            body: data.body,
            targetLanguage: data.targetLanguage,
            statementCustom: data.statementCustom,
            statementStart: data.statementStart,
            statementEnd: data.statementEnd,
          },
        });
      } catch (error) {
        notifyError(t('reviewDetail.notify.emailFailed', { message: error instanceof Error ? error.message : t('common.unknownError') }));
        throw error;
      }
    };
    // The completion is the ordinary one: notes saved first, then the server's completion guard.
    const outcome = await sendThenComplete({ alsoComplete: data.alsoComplete, send, complete: handleCompleteReview });
    if (outcome.completed) notifySuccess(t('reviewDetail.notify.emailSentAndCompleted'));
    else if (!data.alsoComplete) notifySuccess(t('reviewDetail.notify.emailSent'));
    else notifyError(t('reviewDetail.notify.completeAfterSendFailed', {
      message: outcome.completeError instanceof Error ? outcome.completeError.message : t('common.unknownError'),
    }));
  };

  const handleCreateActionItem = async (data: {
    title: string;
    description: string;
    category: ActionItemCategory;
    priority: ActionItemPriority;
    dueDate?: string;
  }) => {
    if (!review) return;
    try {
      await createActionItemMutation.mutateAsync({
        sourceType: 'client-review',
        sourceReviewId: review.id,
        sourceClientId: review.clientId,
        sourceClientName: review.clientName,
        bookkeeperId: review.bookkeeperId,
        bookkeeperName: review.bookkeeperName,
        supervisorId: currentUser?.localAccountId || '',
        supervisorName: currentUser?.name || 'Supervisor',
        title: data.title,
        description: data.description,
        category: data.category,
        priority: data.priority,
        dueDate: data.dueDate,
        createdInMeetingId: '', // No meeting context for review-created items
      });
      setShowAddActionItemDialog(false);
      notifySuccess(t('reviewDetail.notify.actionCreated'));
    } catch (error) {
      notifyError(t('reviewDetail.notify.actionFailed', { message: error instanceof Error ? error.message : t('common.unknownError') }));
    }
  };

  const handleUpdateReviewType = async (newType: 'quarterly' | 'year-end') => {
    if (!id || !review) return;
    await updateReviewMutation.mutateAsync({
      id,
      data: { reviewType: newType },
    });
    setShowEditTypeDialog(false);
  };

  // "Archive" used to PATCH {status:'cancelled'} with no reason, actor or time — 3 of the 7
  // cancelled reviews in production carry no reason for exactly that cause. Cancelling now goes
  // through POST /cancel (spec §10.6), which requires a reason and records who and when.
  const openCancelDialog = (reason?: string) => {
    setCancelInitialReason(reason);
    setShowCancelDialog(true);
  };

  const handleUpdateSupervisor = async () => {
    const person = reviewerOptions?.find((u) => u.systemuserid === newSupervisorId);
    if (!id || !person) return;
    await reassignReviewer.mutateAsync({ id, supervisorId: person.systemuserid, supervisorName: person.fullname });
    setShowSupervisorDialog(false);
    setNewSupervisorId('');
  };

  if (isLoading) {
    return <PageSkeleton variant="detail" />;
  }

  if (error || !review) {
    return (
      <Card style={{ padding: '24px', textAlign: 'center' }}>
        <Title1>{t('reviewDetail.notFound.title')}</Title1>
        <Text style={{ color: tokens.colorNeutralForeground3 }}>
          {error instanceof Error ? error.message : t('reviewDetail.notFound.body')}
        </Text>
        <Button as="a" appearance="primary" {...reviewsLink} style={{ marginTop: '16px' }}>
          {t('reviewDetail.notFound.back')}
        </Button>
      </Card>
    );
  }

  const isCompleted = review.status === 'completed';
  const isPending = review.status === 'pending';
  const checkedCount = review.checklistItems.filter(i => i.isChecked).length;
  const totalCount = review.checklistItems.length;
  const progressPercent = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;

  // Workflow validation
  const hasDocuments = review.financialDocuments.length > 0;
  const hasActionItems = (reviewActionItems?.length ?? 0) > 0;
  const hasEmailSent = review.emailsSent.length > 0;
  // One gate decides Send to Client, and says why when it is closed (S3). An action item is resolved
  // when it is completed OR cancelled, the server's terminal set; a cancelled item used to block forever.
  const sendGate = sendToClientGate({
    isCompleted,
    documentCount: review.financialDocuments.length,
    actionItems: reviewActionItems,
  });
  const actionItemsResolved = sendGate.blocking.length === 0;
  // Unknown action items keep Complete closed unless the email went out: completing on a list that never loaded
  // could close a review that still has open items.
  const canCompleteReview = hasEmailSent || (reviewActionItems !== null && !hasActionItems && !hasDocuments);

  return (
    <div>
      {/* Pinned client announcements — cannot-miss, above the header */}
      <ClientAnnouncementBanner companyId={review.clientId} />

      {/* Month coverage (spec §10.4) and the cancellation record (§10.6) */}
      <CancellationNote review={review} />
      <ReinstateOffer review={review} />
      <CoverageBanner review={review} onCancelCovered={() => openCancelDialog('covered-by-another-review')} />
      <MergeOffer review={review} />
      <NextUpBanner review={review} />
      <MonthStrip review={review} readiness={monthReadiness} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button
            as="a"
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            aria-label={t('reviewDetail.notFound.back')}
            {...reviewsLink}
          />
          <div>
            <Title1>{review.clientName}</Title1>
            <Text style={{ color: tokens.colorNeutralForeground3 }}>
              {t('reviewDetail.period', { range: formatPeriodRange(review.periodStart, review.periodEnd) })}
            </Text>
            {(review.status === 'pending' || review.status === 'in-progress') && (
              <Button
                size="small"
                appearance="transparent"
                icon={<EditRegular />}
                onClick={() => setShowEditPeriodDialog(true)}
                style={{ marginLeft: '4px' }}
              >
                {t('reviewDetail.editPeriod')}
              </Button>
            )}
            <PeriodHistoryNote review={review} />
            {/* Authorship — the record is quality-control evidence, so who created it is part of
                the evidence. Distinct from the assigned reviewer shown below. */}
            <Text block size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {t('reviewDetail.createdBy', { author: formatReviewAuthor(review) })}
              {review.lifecycleStatus === 'scheduled' && review.readyOn
                ? ` · ${t('reviewDetail.notActionableUntil', { date: formatCalendarDate(review.readyOn) })}`
                : ''}
              {review.lifecycleStatus === 'waiting-on-bookkeeping' && review.waitingOnMonths?.length
                ? ` · ${t('reviewDetail.waitingOnBookkeeping', {
                    months: monthsAsWords(review.waitingOnMonths),
                    count: review.waitingOnMonths.length,
                  })}`
                : ''}
            </Text>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {getStatusBadge(review.lifecycleStatus ?? review.status)}
          <Badge appearance="outline" color={review.reviewType === 'year-end' ? 'important' : 'informative'}>
            {review.reviewType === 'year-end' ? t('reviewType.yearEnd') : t('reviewType.quarterly')}
          </Badge>
          {isPending && (
            <Button
              appearance="primary"
              icon={<PlayRegular />}
              onClick={handleStartReview}
              disabled={startReviewMutation.isPending}
            >
              {t('reviewDetail.start')}
            </Button>
          )}
          {!isCompleted && !isPending && (
            <Tooltip
              content={!canCompleteReview ? t('reviewDetail.completeBlocked') : t('reviewDetail.completeHint')}
              relationship="description"
            >
              <Button
                appearance="primary"
                icon={<CheckmarkCircleRegular />}
                onClick={handleCompleteReview}
                disabled={completeReviewMutation.isPending || !canCompleteReview}
              >
                {t('reviewDetail.complete')}
              </Button>
            </Tooltip>
          )}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="subtle" icon={<MoreHorizontalRegular />} />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<EditRegular />} onClick={() => setShowEditTypeDialog(true)}>
                  {t('reviewDetail.menu.changeType')}
                </MenuItem>
                {/* "Bring to the next meeting" (meetings-agenda spec §5): the bookkeeper's next agenda lists it. */}
                <MenuItem
                  icon={review.meetingFlag ? <FlagOffRegular /> : <FlagRegular />}
                  onClick={() => meetingFlagMutation.mutate({ id: review.id, flagged: !review.meetingFlag })}
                >
                  {review.meetingFlag ? t('reviewDetail.menu.dontBringToMeeting') : t('reviewDetail.menu.bringToMeeting')}
                </MenuItem>
                <MenuItem icon={<PersonRegular />} onClick={() => {
                  setNewSupervisorId(review.supervisorId || '');
                  reassignReviewer.reset();
                  setShowSupervisorDialog(true);
                }}>
                  {t('reviewDetail.menu.changeSupervisor')}
                </MenuItem>
                {(review.status === 'pending' || review.status === 'in-progress') && (
                  <MenuItem icon={<DismissCircleRegular />} onClick={() => openCancelDialog()}>
                    {t('reviewDetail.menu.cancel')}
                  </MenuItem>
                )}
                {/* Admins only, and the server says whether this one may go (spec §10.10). */}
                {review.deleteOption ? (
                  <MenuItem
                    icon={<DeleteRegular />}
                    onClick={() => setShowDeleteDialog(true)}
                    style={{ color: tokens.colorPaletteRedForeground1 }}
                  >
                    {t('reviewDetail.menu.delete')}
                  </MenuItem>
                ) : null}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>

      {/* Client Info Card */}
      <Card style={{ padding: '16px', marginBottom: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '24px' }}>
          <div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>{t('reviewDetail.info.assignedTo')}</Text>
            <Text weight="semibold">{review.supervisorName || t('common.unassigned')}</Text>
          </div>
          <div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>{t('reviewDetail.info.bookkeeper')}</Text>
            <Text weight="semibold">{review.bookkeeperName}</Text>
          </div>
          <div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>{t('reviewDetail.info.complexity')}</Text>
            <Badge
              appearance="filled"
              color={review.complexity === 'high' ? 'danger' : review.complexity === 'medium' ? 'warning' : 'success'}
            >
              {review.complexity
                ? t(`complexity.${review.complexity.toLowerCase()}`, { defaultValue: review.complexity.toUpperCase() })
                : t('common.notAvailable')}
            </Badge>
          </div>
          <div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>{t('reviewDetail.info.avgTransactions')}</Text>
            <Text weight="semibold">
              {t('reviewDetail.info.perMonth', {
                value: review.avgMonthlyTransactions?.toLocaleString(i18n.language) || t('common.notAvailable'),
              })}
            </Text>
          </div>
          <div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>{/* i18n-ignore: product name */}Xero</Text>
            {review.xeroLink ? (
              <Link href={review.xeroLink} target="_blank">
                {t('reviewDetail.info.openInXero')} <OpenRegular style={{ fontSize: '12px' }} />
              </Link>
            ) : (
              <Text style={{ color: tokens.colorNeutralForeground3 }}>{t('reviewDetail.info.notLinked')}</Text>
            )}
          </div>
        </div>
        <Divider style={{ margin: '16px 0' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Text>{t('reviewDetail.progress')}</Text>
          <div style={{ flex: 1, height: '8px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: '4px' }}>
            <div
              style={{
                width: `${progressPercent}%`,
                height: '100%',
                backgroundColor: progressPercent === 100 ? tokens.colorPaletteGreenBackground3 : tokens.colorBrandBackground,
                borderRadius: '4px',
                transition: 'width 0.3s',
              }}
            />
          </div>
          <Text weight="semibold">{checkedCount}/{totalCount} ({progressPercent}%)</Text>
        </div>

        {/* Workflow Steps */}
        {!isCompleted && !isPending && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t('reviewDetail.workflow.label')}</Text>
              <div style={{ display: 'flex', gap: '16px' }}>
                <Badge
                  appearance={progressPercent === 100 ? "filled" : "outline"}
                  color={progressPercent === 100 ? "success" : "warning"}
                >
                  1. {t('reviewDetail.workflow.checklist')} {progressPercent === 100 ? '✓' : `${progressPercent}%`}
                </Badge>
                <Badge
                  appearance={hasDocuments ? "filled" : "outline"}
                  color={hasDocuments ? "success" : "subtle"}
                >
                  2. {t('reviewDetail.workflow.documents')} {hasDocuments ? '✓' : '○'}
                </Badge>
                {hasActionItems && (
                  <Badge
                    appearance={actionItemsResolved ? "filled" : "outline"}
                    color={actionItemsResolved ? "success" : "warning"}
                  >
                    3. {t('reviewDetail.workflow.actionItems')} {actionItemsResolved ? '✓' : '○'}
                  </Badge>
                )}
                <Badge
                  appearance={hasEmailSent ? "filled" : "outline"}
                  color={hasEmailSent ? "success" : "subtle"}
                >
                  {hasActionItems ? '4' : '3'}. {t('reviewDetail.workflow.emailSent')} {hasEmailSent ? '✓' : '○'}
                </Badge>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Main Content */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* Left Column - Tabs */}
        <div>
          <TabList selectedValue={activeTab} onTabSelect={(_, data) => setActiveTab(data.value as string)}>
            <Tab value="checklist">{t('reviewDetail.tabs.checklist')}</Tab>
            <Tab value="documents">{t('reviewDetail.tabs.documents')}</Tab>
            <Tab value="actions">{t('reviewDetail.tabs.actionItems')}</Tab>
          </TabList>

          <div style={{ marginTop: '16px' }}>
            {activeTab === 'checklist' && (
              <Card style={{ padding: '16px' }}>
                <ChecklistSection
                  items={review.checklistItems}
                  onToggle={handleToggleChecklistItem}
                  disabled={isCompleted}
                />
              </Card>
            )}

            {activeTab === 'documents' && (
              <FileUploadSection
                title={t('reviewDetail.financialDocuments')}
                documents={review.financialDocuments}
                onUpload={handleFileUpload}
                onDelete={handleFileDelete}
                disabled={isCompleted}
              />
            )}

            {activeTab === 'actions' && (
              <Card style={{ padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Title3>{t('reviewDetail.actions.title')}</Title3>
                  {!isCompleted && (
                    <Button
                      appearance="primary"
                      size="small"
                      icon={<AddRegular />}
                      onClick={() => setShowAddActionItemDialog(true)}
                    >
                      {t('reviewDetail.actions.add')}
                    </Button>
                  )}
                </div>
                {reviewActionItems === null ? (
                  actionItemsError ? (
                    <LoadFailed onRetry={() => refetchActionItems()} />
                  ) : (
                    <Spinner size="tiny" label={actionItemsLoading ? t('common.loading') : undefined} />
                  )
                ) : reviewActionItems.length === 0 ? (
                  <Text style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>
                    {t('reviewDetail.actions.none')}
                  </Text>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {reviewActionItems.map((item: MeetingActionItem) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px',
                          backgroundColor: item.status === 'completed' ? tokens.colorNeutralBackground3 : tokens.colorNeutralBackground2,
                          borderRadius: '4px',
                          border: `1px solid ${tokens.colorNeutralStroke1}`,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <Text weight="semibold" style={{ textDecoration: item.status === 'completed' ? 'line-through' : 'none' }}>
                              {item.title}
                            </Text>
                            <Badge
                              appearance={item.status === 'completed' ? 'filled' : 'tint'}
                              color={item.status === 'completed' ? 'success' : item.priority === 'high' || item.priority === 'critical' ? 'danger' : 'warning'}
                              size="small"
                            >
                              {item.status === 'completed' ? t('actionItems.completed') : priorityLabel(item.priority)}
                            </Badge>
                          </div>
                          {item.description && (
                            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                              {item.description}
                            </Text>
                          )}
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: '4px' }}>
                            {t('reviewDetail.actions.assignedTo', { name: item.bookkeeperName })}
                          </Text>
                        </div>
                        {item.status !== 'completed' && !isCompleted && (
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<CheckmarkCircleRegular />}
                            onClick={() => completeActionItemMutation.mutateAsync({ id: item.id })}
                          >
                            {t('reviewDetail.actions.complete')}
                          </Button>
                        )}
                        {item.status === 'completed' && (
                          <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>

        {/* Right Column - Notes & Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Quick Actions */}
          <Card style={{ padding: '16px' }}>
            <Title3 style={{ marginBottom: '12px' }}>{t('reviewDetail.quick.title')}</Title3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {review.xeroLink && (
                <Button
                  as="a"
                  appearance="secondary"
                  icon={<OpenRegular />}
                  href={review.xeroLink}
                  target="_blank"
                  rel="noopener"
                  style={{ justifyContent: 'flex-start' }}
                >
                  {t('reviewDetail.quick.openXero')}
                </Button>
              )}
              <SendToClientAction
                gate={sendGate}
                onSend={() => setShowEmailDialog(true)}
                onShowActionItems={() => setActiveTab('actions')}
              />
            </div>
          </Card>

          {/* Notes */}
          <Card style={{ padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <Title3>{t('reviewDetail.notes.title')}</Title3>
              {!isCompleted && (
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<SaveRegular />}
                  onClick={handleSaveNotes}
                  disabled={updateReviewMutation.isPending}
                >
                  {t('common.save')}
                </Button>
              )}
            </div>
            <Field label={t('reviewDetail.notes.supervisor')}>
              <Textarea
                value={notes}
                onChange={(_, data) => setNotes(data.value)}
                rows={4}
                disabled={isCompleted}
                placeholder={t('reviewDetail.notes.supervisorPlaceholder')}
              />
            </Field>
            <Field label={t('reviewDetail.notes.findings')} style={{ marginTop: '12px' }}>
              <Textarea
                value={findings}
                onChange={(_, data) => setFindings(data.value)}
                rows={4}
                disabled={isCompleted}
                placeholder={t('reviewDetail.notes.findingsPlaceholder')}
              />
            </Field>
          </Card>

          {/* Email History */}
          {review.emailsSent.length > 0 && (
            <Card style={{ padding: '16px' }}>
              <Title3 style={{ marginBottom: '12px' }}>{t('reviewDetail.emailHistory.title')}</Title3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {review.emailsSent.map(email => (
                  <div
                    key={email.id}
                    style={{
                      padding: '8px',
                      backgroundColor: tokens.colorNeutralBackground2,
                      borderRadius: '4px',
                    }}
                  >
                    <Text weight="semibold" size={200}>{email.subject}</Text>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                      {t('reviewDetail.emailHistory.to', { email: email.recipientEmail, date: formatInstant(email.sentAt, 'dayYearTime') })}
                    </Text>
                    {/* Recorded since 2026-10-26 (spec §10.13.4); older emails have none and say nothing. */}
                    {email.statementPeriod && (
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }} data-testid="email-statement-period">
                        {t('reviewDetail.emails.statement', { range: runLabel(email.statementPeriod.start, email.statementPeriod.end) })}
                      </Text>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Send Email Dialog */}
      <SendEmailDialog
        open={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        reviewId={review.id}
        clientId={review.clientId}
        clientName={review.clientName}
        reviewPeriodEnd={review.periodEnd}
        onSend={handleSendEmail}
        completion={review.status === 'in-progress' ? { checklistDone: checkedCount, checklistTotal: totalCount } : undefined}
      />

      {/* Add Action Item Dialog */}
      <AddActionItemDialog
        open={showAddActionItemDialog}
        onClose={() => setShowAddActionItemDialog(false)}
        review={{
          id: review.id,
          clientId: review.clientId,
          clientName: review.clientName,
          bookkeeperId: review.bookkeeperId,
          bookkeeperName: review.bookkeeperName,
          periodStart: review.periodStart,
          periodEnd: review.periodEnd,
        }}
        onSubmit={handleCreateActionItem}
        isSubmitting={createActionItemMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <EditPeriodDialog review={review} open={showEditPeriodDialog} onOpenChange={setShowEditPeriodDialog} />
      <CancelReviewDialog
        review={review}
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        initialReason={cancelInitialReason}
      />

      <DeleteReviewDialog
        review={review}
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onCancelInstead={() => openCancelDialog()}
      />

      {/* Edit Review Type Dialog */}
      <Dialog open={showEditTypeDialog} onOpenChange={(_, data) => !data.open && setShowEditTypeDialog(false)}>
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>{t('reviewDetail.type.title')}</DialogTitle>
            <DialogContent>
              <Text style={{ marginBottom: '16px', display: 'block' }}>
                {t('reviewDetail.type.current')}{' '}
                <strong>{review.reviewType === 'year-end' ? t('reviewType.yearEndReview') : t('reviewType.quarterlyReview')}</strong>
              </Text>
              <RadioGroup
                value={review.reviewType}
                onChange={(_, data) => handleUpdateReviewType(data.value as 'quarterly' | 'year-end')}
              >
                <Radio value="quarterly" label={t('reviewDetail.type.quarterlyOption')} />
                <Radio value="year-end" label={t('reviewDetail.type.yearEndOption')} />
              </RadioGroup>
              <Text size={200} style={{ color: tokens.colorPaletteYellowForeground2, marginTop: '12px', display: 'block' }}>
                {t('reviewDetail.type.note')}
              </Text>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('common.close')}</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Change Supervisor Dialog */}
      <Dialog open={showSupervisorDialog} onOpenChange={(_, data) => !data.open && setShowSupervisorDialog(false)}>
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>{t('reviewDetail.supervisor.title')}</DialogTitle>
            <DialogContent>
              <Text style={{ marginBottom: '16px', display: 'block' }}>
                {t('reviewDetail.supervisor.current')} <strong>{review.supervisorName || t('common.notAssigned')}</strong>
              </Text>
              <Field label={t('reviewDetail.supervisor.new')}>
                {/* A RadioGroup, not a controlled Dropdown (this app's render-loop hazard): the list is the
                    handful of staff marked as reviewers, each a person with one id. */}
                <RadioGroup
                  value={newSupervisorId}
                  onChange={(_, data) => setNewSupervisorId(data.value)}
                >
                  {(reviewerOptions ?? []).map((u) => (
                    <Radio key={u.systemuserid} value={u.systemuserid} label={u.fullname} />
                  ))}
                </RadioGroup>
                {!reviewerOptions && reviewerOptionsError && (
                  <Text role="alert" size={200} style={{ display: 'block', color: tokens.colorPaletteRedForeground1 }}>
                    {t('createReview.staffNotLoaded')}
                  </Text>
                )}
              </Field>
              {reassignReviewer.isError && (
                <Text role="alert" style={{ color: tokens.colorPaletteRedForeground1, display: 'block', marginTop: '8px' }}>
                  {reassignReviewer.error instanceof Error ? reassignReviewer.error.message : t('reviewDetail.supervisor.failed')}
                </Text>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('common.cancel')}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleUpdateSupervisor}
                disabled={!newSupervisorId || newSupervisorId.toLowerCase() === (review.supervisorId ?? '').toLowerCase() || reassignReviewer.isPending}
              >
                {reassignReviewer.isPending ? t('common.saving') : t('common.save')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <Dialog
        open={pendingDeleteDocId !== null}
        onOpenChange={(_, data) => !data.open && setPendingDeleteDocId(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('reviewDetail.deleteDocument.title')}</DialogTitle>
            <DialogContent>
              <Text>
                {t('reviewDetail.deleteDocument.body')}
              </Text>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('common.cancel')}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={confirmDeleteDocument}
                disabled={deleteDocumentMutation.isPending}
                style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
              >
                {deleteDocumentMutation.isPending ? t('common.deleting') : t('reviewDetail.deleteDocument.confirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Toaster toasterId={toasterId} position="top-end" />
    </div>
  );
}
